import { ReactNode, useMemo } from "react";
import { Cell, Pie, PieChart, Tooltip } from "recharts";

import { getMarketPoolName } from "domain/synthetics/markets";
import { bigintToNumber } from "lib/numbers";
import { USD_DECIMALS } from "sdk/configs/factors";

import TokenIcon from "components/TokenIcon/TokenIcon";

import { CompositionItem, getCompositionPercentage } from "../hooks/useCompositionData";

interface Props {
  data: CompositionItem[];
  label: ReactNode;
}

export function CompositionDiagram({ data, label }: Props) {
  const chartData = useMemo((): CompositionChartItem[] => {
    const sum = data.reduce((acc, item) => {
      return (
        acc +
        (item.type === "market"
          ? bigintToNumber(item.gmBalanceUsd, USD_DECIMALS)
          : bigintToNumber(item.amount, USD_DECIMALS))
      );
    }, 0);

    return data.map((item) => {
      const value =
        item.type === "market"
          ? bigintToNumber(item.gmBalanceUsd, USD_DECIMALS)
          : bigintToNumber(item.amount, USD_DECIMALS);
      // const color =
      //   (item.type === "market"
      //     ? TOKEN_COLOR_MAP[item.market.indexToken.symbol]
      //     : TOKEN_COLOR_MAP[item.token.symbol]) ?? TOKEN_COLOR_MAP.default;

      const color = item.type === "market" ? "#00BD79" : item.side === "long" ? "#00BD79" : "#EF476F";
      return {
        key: item.type === "market" ? item.market.marketTokenAddress : `${item.token.address}-${item.side}`,
        color,
        value,
        tooltipContent: (
          <TooltipContent
            color={color}
            value={value}
            sum={sum}
            name={item.type === "market" ? getMarketPoolName(item.market).replace(/\s*\/\s*/g, "/") : item.token.symbol}
            symbol={item.type === "market" ? item.market.indexToken.symbol : item.token.symbol}
          />
        ),
      };
    });
  }, [data]);

  return (
    <div>
      <CompositionChart items={chartData} label={label} />
    </div>
  );
}

const TooltipContent = ({
  color,
  value,
  sum,
  name,
  symbol,
}: {
  color: string;
  value: number;
  sum: number;
  name: string;
  symbol: string;
}) => {
  const dotStyle = useMemo(() => ({ backgroundColor: color }), [color]);

  return (
    <div className="flex w-max items-center gap-4">
      <div className="mr-2 h-8 w-8 shrink-0 grow rounded-full" style={dotStyle} />
      <TokenIcon symbol={symbol} displaySize={16} />
      <p className="text-body-medium">{name}:</p>
      <p className="text-body-medium">{getCompositionPercentage(value, sum)}%</p>
    </div>
  );
};

interface CompositionChartProps {
  items: CompositionChartItem[];
  label: ReactNode;
}

type CompositionChartItem = {
  key: string;
  tooltipContent: ReactNode;
  value: number;
  color: string;
};

const CompositionTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload as CompositionChartItem;

    return <div className="rounded-8 bg-gray-dark p-10">{item.tooltipContent}</div>;
  }

  return null;
};

const CELL_STYLE = { cursor: "pointer" };

export function CompositionChart({ items, label }: CompositionChartProps) {
  return (
    <div className="relative z-10 h-[108px] w-[108px]">
      <div className="text-body-small absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] text-center font-medium text-gray-lightest">
        {label}
      </div>
      <PieChart width={108} height={108}>
        <Pie
          data={items}
          dataKey="value"
          outerRadius={54}
          innerRadius={46}
          stroke="var(--color-gray-darker)"
          fill="#00BD79"
          startAngle={0}
          endAngle={360}
          // strokeWidth={items.length <= 1 ? 0 : 1.5}
          strokeWidth={0}
        >
          {items.map((entry) => (
            <Cell key={entry.key} fill={entry.color} style={CELL_STYLE} />
          ))}
        </Pie>

        <Tooltip content={CompositionTooltip} />
      </PieChart>
    </div>
  );
}
